import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@bodega/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ───────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can
 * match **ZERO rows and still return "success"**. This is not a hypothesis: it was measured in
 * `space-academy-app` (same canon, `activity` table from the local stack)—the
 * course is created, 1.03 s later the unit is created, and **meanwhile NO
 * `course/updated` op exists**: the title the author had just written
 * never generated a CRUD operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the
 * local view from the latest checkpoint**. If the checkpoint does not yet contain the
 * newly created row, it is **ABSENT at that instant**—and an
 * UPDATE against it writes nothing, generates no op, and **does not fail**.
 *
 * Why the absence of an error proves absolutely nothing here: the server write path
 * deliberately **always returns 2xx** (a 4xx would block the client queue
 * forever; see `infra/PENDING_WORK.md` §1.5.7). The failure here
 * is DIFFERENT—local, inside SQLite, BEFORE a CRUD op exists—but
 * the criterion is the same: a user write that vanishes without a trace
 * is the worst failure mode.
 *
 * This module's contract: write, **REREAD**, and compare. If the value does
 * not land, retry with backoff; if it still has not landed when the retries are
 * exhausted, **THROW**—so the caller can report it instead of leaving the user looking at a
 * field that does not represent anything saved.
 *
 * ── Scope in `bodega-sync-app` (measured, not assumed) ──────────────────────
 * The only mutations file with `.update(` in production is
 * `packages/shared/src/users/mutations.ts` (5 mutations, all on `user`:
 * `locale`, `font_family`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`). Verified with a repo-wide grep of `.update(`
 * outside tests/e2e: there is no other `.update()` — the file domain
 * (BYOS) that gives this repo its name lives server-side (Rust/axum +
 * sqlx), not as local Drizzle mutations against the PowerSync view.
 *
 * `scope` (tenant isolation, brought from `comandero-app`) and
 * `hasUpdatedAt: false` (tables without that column, from `vigia-fleet-app`) are
 * copied anyway because they are parts of the canon (`listto-app@126dcb4e`), not
 * because this repo needs them today—so it inherits them from the mechanical conversion
 * instead of reinventing them under pressure if it grows to multi-org or new tables
 * without `updated_at`.
 *
 * ── `scope`: tenant isolation, not just correctness ──────────────────────────
 * `options.scope` applies additional equality conditions **to both the UPDATE and
 * the reread**—if it applied to only one of them, the verification
 * would lie. A row that does not satisfy the scope is treated like a
 * missing row: the write is never confirmed and the function throws
 * `LocalWriteLostError`.
 *
 * ── THREE pieces involving `updated_at`, and they are THREE different things ──
 *   1. **Stamping** (`hasUpdatedAt`, default `true`): whether this helper
 *      writes `updated_at` in the `SET` of every attempt.
 *   2. **Ignore any `updated_at` supplied by the `patch`** (always, with no flag):
 *      it is never compared or passed through to SET unchanged.
 *   3. **Verify BY `updated_at`** (`verifyBy: "updated_at"`, opt-in): use
 *      the stamped timestamp as proof of landing instead of comparing the
 *      patch by value—for patches with non-comparable SQL expressions
 *      (atomic increments). Incompatible with `hasUpdatedAt: false`.
 *
 * ── Why there is NO `patchRowsVerified` / `waitForRowsPresent` (batch variants) ─
 * No mutation in `users/mutations.ts` touches more than one row per write
 * (verified: no `inArray` in `packages/shared/src/**\/mutations.ts`), so
 * bringing the batch primitive here would be code with no caller. A signal that
 * would justify reopening this: a mutation that starts moving more than one row with the
 * same timestamp.
 *
 * ── Second class: an upsert that chooses INSERT-vs-UPDATE with its own SELECT ──
 *
 * `patchRowVerified`/`waitForRowPresent` protect the write against a row
 * whose `id` is ALREADY known. There is a different class that neither covers:
 * an upsert that decides whether to INSERT or UPDATE with its own SELECT because there is no
 * `id` to reread until that branch is chosen. That SELECT falls into the SAME
 * checkpoint window: if the row exists but has just reappeared after a
 * rebuild, the SELECT sees it as missing and the code INSERTs a new row—the data
 * is not lost, the row is DUPLICATED, and no error is visible.
 *
 * Searched for in this repo (`git grep -n -B8 '\.insert(' packages/shared/src/**\/mutations.ts`,
 * keeping the places above that have a `select(...).where(and(eq(...)))`
 * that is not by PK): **absent**. The only apparent candidate is the
 * `ThemeProvider` effect (`apps/web/src/providers/theme-provider.tsx`) that seeds the
 * `user` row—but this is not that class: it only chooses INSERT-if-missing (with no
 * UPDATE branch), and because the inserted `id` is the `uid` (the PK), a collision with
 * a row that did exist does not duplicate anything—it raises `UNIQUE constraint
 * failed`, which the effect itself already catches and treats as benign ("created
 * concurrently by sync"). No duplicate row is possible: the `id` is
 * deterministic, not a content key that the code could use to generate
 * two different rows for the "same" entity.
 *
 * `readMatchingRowVerified` is likewise copied, **kept here unused** (as in the
 * `listto-app` canon, which does not use it either): if this repo gains a content-key
 * upsert, it inherits the mechanical conversion. The full guide (including
 * the criterion for choosing which of the two ways to close Class 2) is in
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md`.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

/**
 * Retry backoff. Total ≈ 6.05 s: more than enough for a checkpoint round trip
 * a checkpoint (measured at ~1 s against local stack) without leaving a
 * interaction from hanging forever if the row truly does not exist.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition—deep tenant isolation. */
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
  /**
   * Additional conditions the row must ALSO satisfy, besides `id`—
   * typically `organization_id`. They apply to both the UPDATE and the
   * reread: a row that does not satisfy the scope is treated like a
   * (see the security note above).
   /** Waits for the app to finish booting (skeleton overlay disappears). */
  scope?: readonly ScopeCondition[];
  /**
   * Whether the table has an `updated_at` column. Defaults to `true` (the
   * usual behavior: it is stamped on every attempt). Set it to `false`
   * on tables WITHOUT that column—with `false`, `patchRowVerified`
   * never adds it to the `SET`, and `verifyBy: "updated_at"` is no longer valid
   * (there is no column to compare).
   /** Waits for the app to finish booting (skeleton overlay disappears). */
  hasUpdatedAt?: boolean;
  /**
   * How to verify that the write landed when the patch cannot be
   * compared by value (e.g. an atomic SQL increment). EXPLICIT mode,
   * never automatic. `"updated_at"` compares the timestamp that this
   * helper stamps on every attempt: it proves THIS write landed, not
   * that the final value is the expected one. Requires `hasUpdatedAt` other than
   * `false`.
   /** Waits for the app to finish booting (skeleton overlay disappears). */
  verifyBy?: "updated_at";
}

/** Error from a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` with no scope. */
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads the row by id (+ `scope`, if given), or `undefined` if it is not in the
 * local view or does not satisfy the scope—the same result as a "missing row",
 * deliberately (see the security note above).
 /** Waits for the app to finish booting (skeleton overlay disappears). */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`: the select
  // resolves to `{ [x: string]: any }[]`, so annotating the destination is enough—no
  // need to go through `unknown`; that would narrow without any real check.
  const rows: Record<string, unknown>[] = await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1);
  return rows[0];
}

/** `null` and `undefined` represent the same "empty" value when compared with SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * `UPDATE … WHERE id = ?` **verified**: writes, reads back, compares the patch
 * the patch and retries while the fields have not landed. Throws `LocalWriteLostError`
 * if they still have not landed after the backoff is exhausted.
 *
 * If `options.hasUpdatedAt` is not `false` (the normal case), this function sets
 * `updated_at` on every attempt—any `updated_at` supplied by the
 * `patch` itself is ALWAYS ignored, both when writing and when comparing; the rest of
 * the `patch` must contain only domain fields. With `hasUpdatedAt:
 * false` (tables without that column), the `SET` does not touch it at all. With
 * `options.verifyBy === "updated_at"`, the patch is not compared by value:
 * it checks that THIS attempt's timestamp landed—combining that with
 * `hasUpdatedAt: false` is a configuration error and throws immediately,
 * before writing anything.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];
  const hasUpdatedAt = options.hasUpdatedAt ?? true;
  if (options.verifyBy === "updated_at" && !hasUpdatedAt) {
    throw new Error(
      'verifyBy: "updated_at" is incompatible with hasUpdatedAt: false — the table does not have that column to compare.',
    );
  }
  const { updated_at: _ignoredUpdatedAt, ...domainPatch } = patch;

  for (let attempt = 0; ; attempt++) {
    const stampedAt = hasUpdatedAt ? nowISO() : undefined;
    await db
      .update(table)
      .set(hasUpdatedAt ? { ...domainPatch, updated_at: stampedAt } : domainPatch)
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    const landed =
      row !== undefined &&
      (options.verifyBy === "updated_at"
        ? sameValue(row.updated_at, stampedAt)
        : Object.entries(domainPatch).every(([key, value]) => sameValue(row[key], value)));
    if (landed) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(domainPatch));
    }
    await sleep(wait);
  }
}

/**
 * Waits until the row EXISTS in the local view before touching it (+ `scope`,
 * if given, with the same tenant-isolation criterion as `patchRowVerified`).
 *
 * For mutations that are not a simple patch, inside a transaction: retrying there
 * is invalid (it would sleep while holding the write lock), so the wait happens
 * OUTSIDE the transaction. This repo currently has no transactional mutation
 * transactional in `users/mutations.ts` (verified: no `db.transaction()`),
 * so it has no caller yet—it is copied because it is part of the canon.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}

/**
 * Re-reads, with the same backoff as the rest of the module, a row that is
 * identified by CONTENT instead of `id`—the Class 2 documented above
 * (an upsert that chooses INSERT-vs-UPDATE with a SELECT over content
 * content, without an `id` known in advance). **Absent today in
 * in `bodega-sync-app`** (see the "Second class" note above)—is kept here unused, without
 * a caller, so whoever needs it can inherit it instead of reinventing it under
 * pressure.
 *
 * It is ALWAYS called before opening the transaction that chooses INSERT-vs-UPDATE,
 * and NEVER inside it—for the same reason as `waitForRowPresent`. When there are
 * several rows to resolve, resolve them in PARALLEL (`Promise.all`) before
 * the transaction.
 *
 * Unlike `patchRowVerified`/`waitForRowPresent`, exhausting the backoff
 * is NOT an error here: genuine absence (the first time that content
 * combination is touched) is a VALID result, so this function
 * returns `undefined` instead of throwing.
 *
 * `read` must repeat the SAME query on every attempt—a function that
 * rebuilds the `where` against `db`/`trx`, not an already-resolved promise
 * that would be reused without querying the database again.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function readMatchingRowVerified<T>(
  read: () => Promise<T[]>,
  options: VerifiedWriteOptions = {},
): Promise<T | undefined> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    const [row] = await read();
    if (row) {
      return row;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      return undefined;
    }
    await sleep(wait);
  }
}
